import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articlesStore, localizeArticle } from "@/store/articlesStore";
import type { ManagedArticle } from "@/store/articlesStore";
import { useInView } from "@/hooks/useInView";
import { useCategoryLocalize } from "@/hooks/useCategoryLocalize";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getSectionFlags } from "@/store/siteSettingsStore";
import { formatArticleDate } from "@/lib/dateFormatter";
import { throttle } from "@/lib/throttle";
import { onRealtimeEvent } from "@/lib/realtimeManager";

interface LatestNewsProps {
  articles: ManagedArticle[];
  loading: boolean;
  bindingSlug?: string;
}

export default function LatestNews({ articles: published, loading: parentLoading, bindingSlug }: LatestNewsProps) {
  const { t, i18n } = useTranslation();
  const localizeCat = useCategoryLocalize();
  const [sectionRef, inView] = useInView<HTMLElement>({ rootMargin: "400px", fallbackTimeout: 800 });
  const { settings } = useSiteSettings();
  const flags = getSectionFlags(settings?.articleDisplayFlags, "latestNews");
  const [boundArticles, setBoundArticles] = useState<ManagedArticle[]>([]);
  const [boundLoading, setBoundLoading] = useState(false);

  // If bindingSlug is set (non-empty), load articles independently from Supabase
  const loadBound = useCallback(async () => {
    if (!bindingSlug) return;
    setBoundLoading(true);
    try {
      const data = await articlesStore.getByCategoryOrSecondary(bindingSlug, 12);
      setBoundArticles(data.map((article) => localizeArticle(article, i18n.language)));
    } catch {
      setBoundArticles([]);
    } finally {
      setBoundLoading(false);
    }
  }, [bindingSlug, i18n.language]);

  useEffect(() => {
    loadBound();
    const throttledLoad = throttle(loadBound, 3000);
    const unsub = onRealtimeEvent("alsiyahiah:articles:updated", throttledLoad);
    return unsub;
  }, [loadBound]);

  // Decide which articles to use
  const isAll = !bindingSlug;
  const loading = isAll ? parentLoading : boundLoading;
  const sourceArticles = isAll ? published : boundArticles;

  // getByCategoryOrSecondary already filters by category, so just filter by status
  const latest = [...sourceArticles]
    .filter((a) => a.status === "published" || a.status === "featured")
    .sort((a, b) => b.id - a.id)
    .slice(0, 6);

  if (loading) {
    return (
      <section ref={sectionRef} className="bg-[#F8F9FA] py-16">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-1 h-7 bg-red-500 rounded-full" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Cairo']">
                {t("latest.title")}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100">
                <div className="aspect-[16/9] bg-gray-100 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-1/3" />
                  <div className="h-5 bg-gray-100 rounded animate-pulse" />
                  <div className="h-5 bg-gray-100 rounded animate-pulse w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!loading && latest.length === 0) return null;

  const archiveHref = bindingSlug ? `/category/${bindingSlug}` : "/category/aviation";

  return (
    <section ref={sectionRef} className="bg-[#F8F9FA] py-16">
      <div className="max-w-screen-xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-1 h-7 bg-red-500 rounded-full" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Cairo']">
              {t("latest.title")}
            </h2>
          </div>
          <Link
            to={archiveHref}
            className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-[#D4AF37] transition-colors cursor-pointer whitespace-nowrap border border-gray-200 px-4 py-2 rounded-full hover:border-[#D4AF37]"
          >
            {t("latest.archive")}
            <i className="ri-archive-line mr-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {latest.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.slug}`}
              className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="overflow-hidden aspect-[16/9]">
                <img
                  src={article.thumbnailImage || article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  {flags?.showCategory !== false && (
                    <span className="bg-[#D4AF37]/15 text-[#B8941E] text-xs font-semibold px-2.5 py-1 rounded-full">
                      {localizeCat(article.categorySlug, article.category)}
                    </span>
                  )}
                  {flags?.showPublishDate !== false && (
                    <span className="flex items-center gap-1 text-gray-400 text-xs">
                      <i className="ri-calendar-line" />
                      {formatArticleDate(article.date, i18n.language)}
                    </span>
                  )}
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug group-hover:text-[#D4AF37] transition-colors font-['Cairo'] line-clamp-2 mb-3">
                  {article.title}
                </h3>
                <div className="flex items-center gap-2 text-gray-400 text-xs pt-3 border-t border-gray-50">
                  {flags?.showReadTime !== false && (
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line" />
                      {article.readTime} {t("latest.readTime")}
                    </span>
                  )}
                  <span className="mr-auto flex items-center gap-1 text-[#D4AF37] font-medium text-xs group-hover:gap-2 transition-all">
                    {t("latest.readMore")} <i className="ri-arrow-left-s-line" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
